// What every calendar source shares: whether any is connected, the user's
// calendar days (every calendar not off, from Outlook and CalDAV alike), the
// per-event override on a Mixed calendar, and syncing every connection.

import type { CalendarEvent, CalendarRole } from '@cerebro-diem/core';
import { supabase } from './supabase';
import { caldavConnection, syncCaldav, type CaldavConnection } from './caldav';
import { outlookConnection, syncOutlook, type OutlookConnection } from './outlook';

/** Every calendar query lives under one root, so one invalidation refreshes them all. */
export const calendarKeys = {
  all: ['calendar'] as const,
  connections: ['calendar', 'connections'] as const,
  days: (from: string, to: string) => ['calendar', 'days', from, to] as const,
};

export interface CalendarConnections {
  outlook: OutlookConnection | null;
  caldav: CaldavConnection | null;
}

export async function calendarConnections(): Promise<CalendarConnections> {
  const [outlook, caldav] = await Promise.all([outlookConnection(), caldavConnection()]);
  return { outlook, caldav };
}

export const anyCalendarConnected = (c: CalendarConnections | undefined): boolean => !!(c?.outlook || c?.caldav);

/** The sources whose saved credentials were refused, by the name the user knows them by. */
export function sourcesNeedingReconnect(c: CalendarConnections | undefined): string[] {
  return [c?.outlook?.needs_reconnect ? 'Outlook' : null, c?.caldav?.needs_reconnect ? 'Your CalDAV server' : null]
    .filter((s): s is string => s !== null);
}

/** Sync every connection that can sync; the first failure is thrown after all were tried. */
export async function syncCalendars(c: CalendarConnections): Promise<void> {
  const runs: Promise<unknown>[] = [];
  if (c.outlook && !c.outlook.needs_reconnect) runs.push(syncOutlook());
  if (c.caldav && !c.caldav.needs_reconnect) runs.push(syncCaldav());
  const failed = (await Promise.allSettled(runs)).find((r): r is PromiseRejectedResult => r.status === 'rejected');
  if (failed) throw failed.reason;
}

/** The user's events on the local days `from` up to (not including) `to`, both YYYY-MM-DD. */
export async function calendarDays(from: string, to: string): Promise<CalendarEvent[]> {
  const { data, error } = await supabase.rpc('calendar_days', { p_from: from, p_to: to });
  if (error) throw error;
  return (data ?? []) as CalendarEvent[];
}

/** On a Mixed calendar: false says the event does not block; null returns it to blocking. */
export async function setEventBlocks(id: string, override: false | null): Promise<void> {
  const { error } = await supabase.from('calendar_events').update({ blocks_override: override }).eq('id', id);
  if (error) throw error;
}

/** A calendar's role, whichever source it comes from. */
export async function setCalendarRole(id: string, role: CalendarRole): Promise<void> {
  const { error } = await supabase.from('calendars').update({ role }).eq('id', id);
  if (error) throw error;
}
